/**
 * Onboarding Step - Generic wrapper for module content
 *
 * Provides consistent styling, animations, and progress metrics
 * for each onboarding module.
 */

import { useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import type { ModuleDefinition } from '../../types/onboarding';
import { MODULE_TIME_ESTIMATES } from '../../lib/onboarding/constants';
import { useModuleTimer } from '../../hooks/useOnboardingTelemetry';
import { cn } from '../../lib/utils';

interface OnboardingStepProps {
  step: ModuleDefinition | undefined;
  children: React.ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
}

export default function OnboardingStep({
  step,
  children,
  isFirst = false,
  isLast = false,
}: OnboardingStepProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Track time spent
  const { getTimeSpent } = useModuleTimer(step?.id || 'welcome');

  // Scroll to top on step change
  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step?.id]);

  if (!step) {
    return null;
  }

  const timeEstimate = MODULE_TIME_ESTIMATES[step.id];

  return (
    <div
      ref={containerRef}
      className={cn(
        'min-h-full p-6 md:p-8',
        'animate-in fade-in slide-in-from-right-4 duration-300'
      )}
    >
      <div className="max-w-4xl mx-auto">
        {/* Step Header */}
        <header className="mb-8">
          {/* Time Estimate Badge */}
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs mb-4"
            style={{
              backgroundColor: 'var(--color-accent-muted)',
              color: 'var(--color-accent)',
            }}
          >
            <Clock className="w-3 h-3" />
            <span>~{timeEstimate.min}-{timeEstimate.max} minutes</span>
          </div>

          {/* Title */}
          <h1
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {step.title}
          </h1>

          {/* Description */}
          <p
            className="text-base md:text-lg"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {step.description}
          </p>

          {/* Hard Gate Indicator */}
          {step.hardGate && (
            <div
              className="mt-4 p-3 rounded-lg text-sm flex items-center gap-2"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                borderLeft: '3px solid var(--color-accent)',
              }}
            >
              <span style={{ color: 'var(--color-text-secondary)' }}>
                Required to access the dashboard
              </span>
            </div>
          )}
        </header>

        {/* Module Content */}
        <div className="pb-24">{children}</div>

        {/* Save Reminder (for neurodivergent users) */}
        <div
          className="fixed bottom-24 right-6 px-4 py-2 rounded-full text-xs shadow-lg"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-muted)',
            border: '1px solid var(--color-border)',
          }}
        >
          Progress saved automatically
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components for module content
// ============================================================================

interface StepSectionProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

export function StepSection({
  title,
  description,
  children,
  className,
}: StepSectionProps) {
  return (
    <section className={cn('mb-8', className)}>
      {title && (
        <h2
          className="text-lg font-semibold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {title}
        </h2>
      )}
      {description && (
        <p
          className="text-sm mb-4"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {description}
        </p>
      )}
      {children}
    </section>
  );
}

interface StepCardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'highlight' | 'warning';
}

export function StepCard({
  children,
  className,
  variant = 'default',
}: StepCardProps) {
  const variants = {
    default: {
      backgroundColor: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
    },
    highlight: {
      backgroundColor: 'var(--color-accent-muted)',
      border: '1px solid var(--color-accent)',
    },
    warning: {
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      border: '1px solid var(--color-warning)',
    },
  };

  return (
    <div
      className={cn('p-6 rounded-xl', className)}
      style={variants[variant]}
    >
      {children}
    </div>
  );
}

interface StepActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  children: React.ReactNode;
  className?: string;
}

export function StepActionButton({
  onClick,
  disabled = false,
  variant = 'primary',
  children,
  className,
}: StepActionButtonProps) {
  const variants = {
    primary: {
      backgroundColor: 'var(--color-accent)',
      color: 'var(--color-bg)',
    },
    secondary: {
      backgroundColor: 'var(--color-bg-secondary)',
      color: 'var(--color-text-secondary)',
    },
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'px-6 py-3 rounded-xl font-semibold transition-all duration-200',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'hover:scale-105 active:scale-100',
        className
      )}
      style={variants[variant]}
    >
      {children}
    </button>
  );
}

interface StepCheckboxProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}

export function StepCheckbox({
  id,
  label,
  checked,
  onChange,
  description,
}: StepCheckboxProps) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors',
        'hover:bg-opacity-80'
      )}
      style={{
        backgroundColor: checked ? 'var(--color-accent-muted)' : 'var(--color-bg-tertiary)',
      }}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 rounded accent-current"
        style={{ accentColor: 'var(--color-accent)' }}
      />
      <div className="flex-1">
        <span
          className="font-medium text-sm"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {label}
        </span>
        {description && (
          <p
            className="text-xs mt-1"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {description}
          </p>
        )}
      </div>
    </label>
  );
}
